import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { groupNotebook, notebookLinkLabel, notebookRowTime } from '@harvus/shared';
import { B, radius } from '../constants/brand';
import { api } from '../api/client';
import { Btn, Spinner, Icon, useToast } from '../components/ui';
import { NoteBodyEditor, insertMarkdownAtEnd } from '../components/shared/NoteBodyEditor';
import { openNoteCollab } from '../notebook/collab';
import { uploadNotebookFile, makeNotebookFileResolver } from '../notebook/files';
import { InkCanvas } from '../components/shared/InkCanvas';
import WritingTools from '../notebook/WritingTools';
import { runHandwritingOcr } from '../utils/handwritingOcr';
import { getAccessToken } from '../config/auth';
import { todayIsoInZone } from '../utils/format';
import { collabStatusLabel } from '@harvus/shared';
import { LinkPicker, linkChipStyle as chipStyle } from '../notebook/LinkPicker';
import { ConvertFileButton } from '../notebook/ConvertFileButton';
import { convertibleFileId, alreadyTranscribed, convertFileBlock, prepareNoteForRouting } from '@harvus/shared';
import SuggestLinksModal from '../notebook/SuggestLinksModal';

// =============================================================
// NotesApp — the Notes app (web). iOS-Notes-style: a list of the notes you can
// see beside the note you are editing, every note belonging to a person and
// LINKED to records.
//
//   * A note with no link is PRIVATE to its owner — nobody else sees it, Admins
//     included. Linking it to a deal, capital item or task is the sharing act:
//     everyone who can see that record can read and edit it.
//   * LIVE CO-EDITING (migration 233): the note is a Yjs document every open
//     editor shares, with cursors (notebook/collab.js). Without migration 233
//     it falls back to a debounced whole-document autosave, where a save made
//     against an older copy answers 409 and the page says so rather than
//     silently overwriting a colleague's edit.
//   * FILES SIT INSIDE THE NOTE (migration 234): an image, a sketch or any
//     file is a BlockNote block where it was dropped — Attach, Draw (the
//     shared InkCanvas: keep the drawing, or convert handwriting to text), drag,
//     paste or "/image". The block stores a stable reference that is resolved
//     to a short read link on render (notebook/files.js).
//   * Harvi writing tools (notebook/WritingTools.jsx) and the inline
//     calculator (`1,200 * 12 =` writes 14,400; NoteBodyEditor mathAssist).
//   * CONVERT HANDWRITING TO TEXT on a PDF or photo already in the note: the
//     button in the toolbar over a selected file. The server reads the file
//     from storage (POST /api/notebook-files/{id}/ocr) and the text goes in
//     right under the file, headed "Transcribed from …" so nobody mistakes it
//     for typed text. The file stays as the original to check against.
//   * FILE THIS NOTE (the Harvi menu): "Suggest links & tasks" links this note
//     to the deals it is about — nothing copied — and "Split & route" opens the
//     Brain-dump splitter on it. Both READ THE NOTE'S FILES FIRST: any PDF or
//     photo not yet transcribed is converted into the note, so the router sees
//     what the documents say, and a split attaches each file to the routed
//     notes it fed (shared/src/noteRouting.ts). The header's route button
//     opens a blank Brain-dump.
//   * Recently deleted keeps your own deleted notes for 30 days.
//
// API: api/src/functions/notebook.js. Grouping: shared/src/notebook.ts.
// The brain-dump capture funnel (GlobalNotes) is one click away in the header.
// =============================================================

const VIEWS = [
  { id: 'all', label: 'All notes', icon: 'note' },
  { id: 'private', label: 'Private', icon: 'lock' },
  { id: 'shared', label: 'Linked to records', icon: 'link' },
  { id: 'deleted', label: 'Recently deleted', icon: 'trash' },
];

const SAVE_DELAY_MS = 900;

export default function NotesApp({ onOpenCapture, initialNoteId, onInitialNoteHandled }) {
  const { toast } = useToast();
  const [view, setView] = useState('all');
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [privateAvailable, setPrivateAvailable] = useState(true);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ view, limit: '300' });
      if (q.trim()) params.set('q', q.trim());
      const res = await api.get(`notebook?${params}`);
      setItems(res.items || []);
      setPrivateAvailable(res.privateNotesAvailable !== false);
      setListError(null);
    } catch (e) {
      setListError(e.message || 'Could not load notes.');
    } finally {
      setLoading(false);
    }
  }, [view, q]);

  useEffect(() => {
    const t = setTimeout(loadList, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [loadList, q]);

  const sections = useMemo(() => groupNotebook(items), [items]);

  // Opened on a particular note (Quick Note's "Open in Notes").
  useEffect(() => {
    if (!initialNoteId) return;
    setView('all');
    setSelectedId(initialNoteId);
    onInitialNoteHandled?.();
  }, [initialNoteId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep a row in the list in step with what the editor saved, without a reload.
  const patchItem = useCallback((id, patch) => {
    setItems(prev => prev.map(n => (n.id === id ? { ...n, ...patch } : n)));
  }, []);

  const createNote = async () => {
    try {
      const res = await api.post('notebook', { content: '', links: [], clientRequestId: crypto.randomUUID() });
      if (view === 'deleted') setView('all');
      await loadList();
      setSelectedId(res.id);
    } catch (e) {
      toast(e.message || 'Could not create a note.', 'error');
    }
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', minHeight: 'calc(100vh - 120px)', background: B.surface, border: `1px solid ${B.divider}`, borderRadius: radius.xl, overflow: 'hidden' }}>
      {/* Views */}
      <nav aria-label="Note views" style={{ flex: '1 1 200px', maxWidth: 230, minWidth: 190, background: B.surfaceMuted, borderRight: `1px solid ${B.divider}`, padding: '16px 10px', display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 6px 10px' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: B.textMd, letterSpacing: '0.04em' }}>NOTES</span>
          <button type="button" aria-label="New note" onClick={createNote}
            style={{ width: 34, height: 34, border: 0, borderRadius: 8, background: 'transparent', color: B.navy, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="edit" size={18} />
          </button>
        </div>
        {VIEWS.map(v => (
          <button key={v.id} type="button" onClick={() => { setView(v.id); setSelectedId(null); }}
            aria-current={view === v.id ? 'page' : undefined}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, border: 0,
              background: view === v.id ? B.navy : 'transparent', color: view === v.id ? B.white : B.text,
              fontSize: 14, fontWeight: view === v.id ? 600 : 500, cursor: 'pointer', textAlign: 'left',
            }}>
            <Icon name={v.icon} size={15} />{v.label}
          </button>
        ))}
        {onOpenCapture && (
          <button type="button" onClick={() => onOpenCapture()}
            style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, border: `1px dashed ${B.border}`, background: 'transparent', color: B.textMd, fontSize: 13, cursor: 'pointer', textAlign: 'left' }}>
            <Icon name="sparkles" size={15} />Brain-dump &amp; route
          </button>
        )}
      </nav>

      {/* List */}
      <section aria-label="Notes" style={{ flex: '1 1 280px', maxWidth: 360, minWidth: 250, borderRight: `1px solid ${B.divider}`, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ padding: '14px 14px 8px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: B.text }}>{VIEWS.find(v => v.id === view)?.label}</h1>
            <span style={{ fontSize: 12, color: B.textLt }}>{items.length} {items.length === 1 ? 'note' : 'notes'}</span>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 10, background: B.surfaceMuted, color: B.textMd }}>
            <Icon name="search" size={15} />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search notes"
              aria-label="Search notes"
              style={{ border: 0, background: 'transparent', font: 'inherit', fontSize: 14, flex: 1, outline: 'none', color: B.text }} />
          </label>
          {view === 'deleted' && (
            <span style={{ fontSize: 12, color: B.textMd }}>Your deleted notes stay here for 30 days.</span>
          )}
        </div>
        <div style={{ overflowY: 'auto', flex: 1, paddingBottom: 12 }}>
          {loading && !items.length && <div style={{ padding: 16 }}><Spinner /></div>}
          {listError && <div style={{ padding: 16, color: B.red, fontSize: 13 }}>{listError}</div>}
          {!loading && !listError && !items.length && (
            <div style={{ padding: 24, color: B.textMd, fontSize: 14, textAlign: 'center' }}>
              {q ? `No notes match "${q}".` : view === 'deleted' ? 'Nothing deleted.' : 'No notes yet.'}
            </div>
          )}
          {sections.map(sec => (
            <div key={sec.key}>
              <div style={{ padding: '12px 16px 4px', fontSize: 12, fontWeight: 700, color: B.textMd }}>{sec.title}</div>
              {sec.items.map(n => (
                <NoteRow key={n.id} note={n} active={n.id === selectedId} onOpen={() => setSelectedId(n.id)} />
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* Editor */}
      <main style={{ flex: '999 1 520px', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {selectedId ? (
          <NoteEditorPane
            key={selectedId}
            noteId={selectedId}
            privateAvailable={privateAvailable}
            onSaved={patchItem}
            onGone={() => { setSelectedId(null); loadList(); }}
            onChanged={loadList}
            onOpenCapture={onOpenCapture}
          />
        ) : (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, color: B.textMd, padding: 32 }}>
            <Icon name="note" size={32} color={B.textLt} />
            <span style={{ fontSize: 15 }}>Select a note, or start a new one.</span>
            <Btn onClick={createNote}>New note</Btn>
          </div>
        )}
      </main>
    </div>
  );
}

function NoteRow({ note, active, onOpen }) {
  return (
    <button type="button" onClick={onOpen} aria-current={active ? 'true' : undefined}
      style={{
        display: 'flex', flexDirection: 'column', gap: 3, width: 'calc(100% - 16px)', margin: '0 8px', padding: '10px 10px',
        border: 0, borderRadius: 10, textAlign: 'left', cursor: 'pointer',
        background: active ? '#fff3cc' : 'transparent', color: B.text,
      }}>
      <span style={{ fontSize: 14, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
        {note.pinned && <Icon name="pushpin" size={12} style={{ marginRight: 4, verticalAlign: -1 }} />}
        {note.title || 'New Note'}
      </span>
      <span style={{ fontSize: 13, color: B.textMd, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
        <span style={{ color: B.text }}>{notebookRowTime(note.modifiedUTC)}</span>&nbsp;&nbsp;{note.snippet || 'No additional text'}
      </span>
      <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
        {note.isPrivate ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: B.textMd }}>
            <Icon name="lock" size={11} />Private
          </span>
        ) : note.links.slice(0, 2).map(l => (
          <span key={`${l.type}:${l.id}`} style={{ ...chipStyle(l.type), fontSize: 11, padding: '2px 7px' }}>{notebookLinkLabel(l)}</span>
        ))}
        {!note.isPrivate && note.links.length > 2 && <span style={{ fontSize: 11, color: B.textMd }}>+{note.links.length - 2}</span>}
      </span>
    </button>
  );
}

function NoteEditorPane({ noteId, privateAvailable, onSaved, onGone, onChanged, onOpenCapture }) {
  // privateAvailable: pre-232 a note cannot lose its last link.
  const { toast } = useToast();
  const [note, setNote] = useState(null);
  const [error, setError] = useState(null);
  const [saveState, setSaveState] = useState('saved'); // saved | dirty | saving | conflict | error
  const [conflict, setConflict] = useState(null);
  const [linking, setLinking] = useState(false);
  const [epoch, setEpoch] = useState(0); // remounts the editor with new content
  const seeded = useRef(false);
  const pending = useRef(null);
  const base = useRef(null);
  const timer = useRef(null);
  const saving = useRef(false);
  // undefined = connecting · null = no co-editing (whole-document autosave) · object = live
  const [collab, setCollab] = useState(undefined);
  const [collabStatus, setCollabStatus] = useState(null);
  const editorRef = useRef(null);
  const titleTimer = useRef(null);
  const fileInput = useRef(null);
  const [sketching, setSketching] = useState(false);
  const [converting, setConverting] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [readingFile, setReadingFile] = useState(null); // { name, status } while a file's handwriting is read
  const [suggesting, setSuggesting] = useState(null);   // the routing bundle while Suggest links is open
  const resolveFileUrl = useMemo(() => makeNotebookFileResolver(api), []);
  // BlockNote's own upload hook: drag-and-drop, paste and the /image menu.
  const uploadFile = useCallback(async (file) => {
    setUploading(n => n + 1);
    try { return (await uploadNotebookFile(api, noteId, file)).url; }
    catch (e) { toast(e.message || 'Could not add the file.', 'error'); throw e; }
    finally { setUploading(n => n - 1); }
  }, [noteId, toast]);

  // Attach / Sketch: upload, then put a block after the one the cursor is in.
  const addFiles = useCallback(async (files) => {
    const editor = editorRef.current;
    if (!editor || !files?.length) return;
    for (const file of files) {
      setUploading(n => n + 1);
      try {
        const up = await uploadNotebookFile(api, noteId, file);
        let at;
        try { at = editor.getTextCursorPosition().block; } catch { at = editor.document[editor.document.length - 1]; }
        const block = { type: up.blockType, props: { url: up.url, name: up.fileName } };
        const [inserted] = editor.insertBlocks([block], at, 'after');
        if (inserted) { try { editor.setTextCursorPosition(inserted, 'end'); } catch { /* not focusable */ } }
      } catch (e) {
        toast(`${file.name || 'File'}: ${e.message || 'could not be added.'}`, 'error');
      } finally { setUploading(n => n - 1); }
    }
  }, [noteId, toast]);

  // The server reads a note's file (nothing is sent up); the same job-backed
  // read the Brain-dump uses, polled when a long PDF outlives the edge.
  const readFile = useCallback((fileId) => runHandwritingOcr({
    fileId, getAccessToken,
    onStatus: (status) => setReadingFile(r => (r ? { ...r, status } : r)),
  }), []);

  // Convert handwriting to text: the text lands right under the file with a
  // "Transcribed from" line (shared/src/noteRouting.ts convertFileBlock).
  const convertFile = useCallback(async (block) => {
    const editor = editorRef.current;
    if (!editor || !convertibleFileId(block)) return;
    const name = block.props?.name || 'the file';
    if (alreadyTranscribed(editor.document, block)
      && !window.confirm(`${name} has already been converted — the text is right below it. Convert it again and add a second copy?`)) return;
    setReadingFile({ name, status: `Reading ${name}…` });
    try {
      const r = await convertFileBlock(editor, block, readFile);
      if (r === 'empty') toast(`No writing was found in ${name}.`, 'error');
      else toast(`Added the text from ${name}.`);
    } catch (e) {
      toast(e.message || `Could not read ${name}.`, 'error');
    } finally { setReadingFile(null); }
  }, [toast, readFile]);

  // Everything routing needs: the note's PDFs and photos are read into it
  // first, then its text and its files (with what each said) come back.
  const prepareRouting = useCallback(async () => {
    const editor = editorRef.current;
    if (!editor) return null;
    setReadingFile({ name: '', status: 'Reading the note…' });
    try {
      const bundle = await prepareNoteForRouting(editor, {
        readFile, onStatus: (status) => setReadingFile(r => (r ? { ...r, status } : r)),
      });
      if (!bundle.text) { toast('There is no text to read yet.', 'error'); return null; }
      return bundle;
    } finally { setReadingFile(null); }
  }, [readFile, toast]);

  const startSuggest = async () => {
    const bundle = await prepareRouting();
    if (bundle) setSuggesting(bundle);
  };

  const startSplit = async () => {
    const bundle = await prepareRouting();
    if (!bundle) return;
    clearTimeout(timer.current);
    await flush();
    onOpenCapture?.({ ...bundle, sourceNote: { id: noteId, title: note?.title || 'this note', isPrivate: !!note?.isPrivate } });
  };

  useEffect(() => {
    let cancelled = false;
    setNote(null);
    api.get(`notebook/${noteId}`)
      .then(n => { if (!cancelled) { setNote(n); base.current = n.modifiedUTC; } })
      .catch(e => { if (!cancelled) setError(e.status === 404 ? 'This note is no longer available.' : (e.message || 'Could not open the note.')); });
    return () => { cancelled = true; };
  }, [noteId, epoch]);

  const noteLoaded = !!note;
  const noteDeleted = !!note?.isDeleted;
  useEffect(() => {
    if (!noteLoaded) return undefined;
    if (noteDeleted) { setCollab(null); return undefined; }
    let cancelled = false;
    let provider = null;
    setCollab(undefined);
    openNoteCollab({
      api, noteId,
      onStatus: (st) => { if (!cancelled) setCollabStatus(st); },
      // Another writer replaced the note wholesale: reload from Content.
      onReset: () => { if (!cancelled) setEpoch(e => e + 1); },
    }).then(p => {
      if (cancelled) { p?.destroy(); return; }
      provider = p;
      if (p) p.setContentSource(() => JSON.stringify(editorRef.current?.document || []));
      seeded.current = false; // the remounted editor reports its load again
      setCollab(p);
    }).catch(() => { if (!cancelled) setCollab(null); });
    return () => { cancelled = true; clearTimeout(titleTimer.current); provider?.destroy(); };
  }, [noteId, epoch, noteLoaded, noteDeleted]);

  const flush = useCallback(async ({ force = false } = {}) => {
    if (!pending.current || saving.current) return;
    const content = pending.current;
    saving.current = true;
    setSaveState('saving');
    try {
      const res = await api.put(`notebook/${noteId}`, { content, baseModifiedUTC: force ? null : base.current });
      base.current = res.modifiedUTC;
      if (pending.current === content) pending.current = null;
      setSaveState(pending.current ? 'dirty' : 'saved');
      onSaved(noteId, { title: res.title || 'New Note', modifiedUTC: res.modifiedUTC });
    } catch (e) {
      if (e.status === 409) {
        setConflict(e.body?.current || null);
        setSaveState('conflict');
      } else {
        setSaveState('error');
      }
    } finally {
      saving.current = false;
    }
  }, [noteId, onSaved]);

  // Save whatever is pending when the pane unmounts (switching notes).
  useEffect(() => () => { clearTimeout(timer.current); flush(); }, [flush]);

  const onEdit = useCallback(({ content, markdown }) => {
    if (!note || note.isDeleted) return;
    // The editor reports its seed as soon as it loads; opening a note must
    // never save it (a legacy HTML note would be rewritten as JSON) or move it
    // to the top of the list.
    if (!seeded.current) { seeded.current = true; return; }
    if (collab) {
      // Live: Yjs carries the edit; only the list's title needs telling.
      clearTimeout(titleTimer.current);
      titleTimer.current = setTimeout(() => {
        const first = String(markdown || '').split('\n').map(l => l.replace(/^[#>*\-\s\d.[\]]+/, '').trim()).find(Boolean);
        onSaved(noteId, { title: first ? first.slice(0, 120) : 'New Note', modifiedUTC: new Date().toISOString() });
      }, 1200);
      return;
    }
    pending.current = content;
    setSaveState(s => (s === 'conflict' ? s : 'dirty'));
    clearTimeout(timer.current);
    timer.current = setTimeout(() => flush(), SAVE_DELAY_MS);
  }, [note, flush, collab, noteId, onSaved]);

  const updateLinks = async (body) => {
    try {
      const res = await api.put(`notebook/${noteId}/links`, body);
      setNote(n => ({ ...n, links: res.links, isPrivate: res.isPrivate }));
      onChanged();
    } catch (e) {
      toast(e.message || 'Could not change the links.', 'error');
    }
  };

  // Back-dating: the day the note is about, which the deal feed sorts by.
  const setNoteDate = async (value) => {
    const prev = note.noteDate || null;
    const baseBefore = base.current;
    setNote(n => ({ ...n, noteDate: value }));
    try {
      const res = await api.put(`notebook/${noteId}/date`, { noteDate: value });
      // The date write moved ModifiedUTC; it was ours, so the next content
      // save must not read it as somebody else's edit.
      if (base.current === baseBefore) base.current = res.modifiedUTC;
      onChanged();
    } catch (e) {
      setNote(n => ({ ...n, noteDate: prev }));
      toast(e.message || 'Could not change the date.', 'error');
    }
  };

  const togglePin = async () => {
    try {
      const res = await api.put(`notebook/${noteId}/pin`, { pinned: !note.pinned });
      setNote(n => ({ ...n, pinned: res.pinned }));
      onChanged();
    } catch (e) { toast(e.message || 'Could not pin.', 'error'); }
  };

  const remove = async () => {
    const shared = !note.isPrivate;
    if (!window.confirm(shared
      ? 'Delete this note for everyone who can see it? You can restore it from Recently deleted for 30 days.'
      : 'Delete this note? You can restore it from Recently deleted for 30 days.')) return;
    try {
      await api.del(`notebook/${noteId}`);
      onGone();
    } catch (e) { toast(e.message || 'Could not delete.', 'error'); }
  };

  const restore = async () => {
    try {
      await api.post(`notebook/${noteId}/restore`, {});
      toast('Note restored.');
      onGone();
    } catch (e) { toast(e.message || 'Could not restore.', 'error'); }
  };

  if (error) return <div style={{ padding: 32, color: B.textMd }}>{error}</div>;
  if (!note || collab === undefined) return <div style={{ padding: 32 }}><Spinner /></div>;

  const saveLabel = collab
    ? collabStatusLabel(collabStatus)
    : { saved: 'Saved', dirty: 'Editing…', saving: 'Saving…', conflict: 'Not saved', error: 'Save failed — retrying on next edit' }[saveState];

  return (
    <>
      <header style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 20px', borderBottom: `1px solid ${B.divider}` }}>
        <span style={{ fontSize: 12, color: saveState === 'conflict' || saveState === 'error' ? B.red : B.textLt }}>{note.isDeleted ? 'Deleted' : saveLabel}</span>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {note.isDeleted ? (
            note.restorable && <Btn size="sm" onClick={restore}>Restore</Btn>
          ) : (
            <>
              <WritingTools editorRef={editorRef} toast={toast} extraGroups={[{
                id: 'route', title: 'File this note',
                items: [
                  { key: 'suggest', label: 'Suggest links & tasks…', onSelect: startSuggest },
                  ...(onOpenCapture ? [{ key: 'split', label: 'Split & route to deals…', onSelect: startSplit }] : []),
                ],
              }]} />
              {uploading > 0 && <span style={{ fontSize: 12, color: B.textLt }}>Adding file…</span>}
              {readingFile && <span role="status" style={{ fontSize: 12, color: B.textLt }}>{readingFile.status}</span>}
              <IconButton label="Attach a file or photo" icon="attach" onClick={() => fileInput.current?.click()} />
              <IconButton label="Draw or handwrite" icon="pen" active={sketching} onClick={() => setSketching(v => !v)} />
              {onOpenCapture && <IconButton label="Brain-dump & route (blank)" icon="route" onClick={() => onOpenCapture()} />}
              <input ref={fileInput} type="file" multiple hidden
                onChange={(e) => { const f = [...(e.target.files || [])]; e.target.value = ''; addFiles(f); }} />
              <IconButton label={note.pinned ? 'Unpin' : 'Pin'} icon="pushpin" active={note.pinned} onClick={togglePin} />
              <IconButton label="Link to a record" icon="link" onClick={() => setLinking(true)} />
              {note.canDelete && <IconButton label="Delete" icon="trash" onClick={remove} />}
            </>
          )}
        </div>
      </header>

      {!note.isDeleted && (
        note.isPrivate ? (
          <div style={{ margin: '12px 20px 0', padding: '10px 12px', borderRadius: 12, background: B.surfaceMuted, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <Icon name="lock" size={16} color={B.textMd} />
            <span style={{ flex: 1, minWidth: 200, fontSize: 13, color: B.textMd }}>
              Private: only you can see this note. Link it to a deal, capital item or task to share it with that record's team.
            </span>
            <Btn size="sm" onClick={() => setLinking(true)}>Link…</Btn>
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '10px 20px', borderBottom: `1px solid ${B.divider}` }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: B.textMd }}>Linked to</span>
            {note.links.map(l => (
              <span key={`${l.type}:${l.id}`} style={chipStyle(l.type)}>
                {notebookLinkLabel(l)}
                <button type="button" aria-label={`Remove link to ${l.label || 'record'}`}
                  onClick={() => {
                    if (note.links.length === 1 && !privateAvailable) { toast('Private notes are not available until the database update finishes.', 'error'); return; }
                    if (note.links.length === 1 && !window.confirm('Remove the last link? The note becomes private to its owner and disappears for everyone else.')) return;
                    updateLinks({ remove: [{ type: l.type, id: l.id }] });
                  }}
                  style={{ border: 0, background: 'transparent', color: 'inherit', cursor: 'pointer', padding: 0, display: 'flex' }}>
                  <Icon name="close" size={12} />
                </button>
              </span>
            ))}
            <button type="button" onClick={() => setLinking(true)}
              style={{ height: 26, padding: '0 10px', border: `1px dashed ${B.border}`, borderRadius: 999, background: 'transparent', fontSize: 12, color: B.textMd, cursor: 'pointer' }}>
              + Link
            </button>
            <span style={{ fontSize: 12, color: B.textLt, marginLeft: 'auto' }}>Everyone who can see these records can read and edit this note</span>
          </div>
        )
      )}

      {saveState === 'conflict' && (
        <div role="alert" style={{ margin: '12px 20px 0', padding: '10px 12px', borderRadius: 10, background: '#fdecea', color: B.red, fontSize: 13, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: 220 }}>Someone else changed this note while you were editing. Your latest changes are not saved yet.</span>
          <Btn size="sm" variant="secondary" onClick={() => {
            if (!conflict) return;
            pending.current = null;
            seeded.current = false;
            base.current = conflict.modifiedUTC;
            setNote(n => ({ ...n, content: conflict.content, modifiedUTC: conflict.modifiedUTC }));
            setConflict(null);
            setSaveState('saved');
            setEpoch(e => e + 1);
          }}>
            Discard mine, load theirs
          </Btn>
          <Btn size="sm" onClick={() => { setSaveState('dirty'); flush({ force: true }); }}>Keep mine</Btn>
        </div>
      )}

      <article style={{ width: '100%', maxWidth: 780, margin: '0 auto', boxSizing: 'border-box', padding: '16px 20px 48px', flex: 1 }}>
        <div style={{ fontSize: 12, color: B.textLt, textAlign: 'center', marginBottom: 8, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span>{new Date(note.modifiedUTC || note.createdUTC).toLocaleString('en-US', { dateStyle: 'long', timeStyle: 'short' })}</span>
          {!note.isDeleted && (
            <label title="The day this note is about — back-date it for a call or meeting that already happened"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              · Note date
              <input type="date" value={note.noteDate || ''} max={todayIsoInZone()}
                onChange={e => setNoteDate(e.target.value || null)}
                style={{ fontSize: 12, color: B.text, border: `1px solid ${B.border}`, borderRadius: 4, padding: '2px 6px', fontFamily: 'inherit', background: B.white }} />
            </label>
          )}
        </div>
        {sketching && (
          <div style={{ marginBottom: 12 }}>
            <InkCanvas
              busy={converting}
              onClose={() => setSketching(false)}
              onKeepDrawing={({ base64 }) => { setSketching(false); addFiles([base64ToFile(base64, 'drawing.jpg', 'image/jpeg')]); }}
              onConvert={async ({ base64 }) => {
                setConverting(true);
                try {
                  const { text, markdown } = await runHandwritingOcr({ base64, contentType: 'image/jpeg', getAccessToken });
                  const ok = await insertMarkdownAtEnd(editorRef.current, markdown?.trim() ? markdown : text);
                  if (ok) setSketching(false);
                  else toast('No writing was found in the drawing.', 'error');
                } catch (e) {
                  toast(e.message || 'Could not read the handwriting.', 'error');
                } finally { setConverting(false); }
              }}
            />
          </div>
        )}
        <NoteBodyEditor
          key={`${note.id}:${epoch}:${collab ? 'live' : 'solo'}`}
          editorRef={editorRef}
          collaboration={collab ? { fragment: collab.fragment, user: collab.user, provider: collab.provider, showCursorLabels: 'activity' } : undefined}
          initialContent={note.content}
          onChange={onEdit}
          uploadFile={note.isDeleted ? undefined : uploadFile}
          mathAssist={!note.isDeleted}
          resolveFileUrl={resolveFileUrl}
          formattingToolbarExtras={note.isDeleted ? undefined : [
            <ConvertFileButton key="convertFile" onConvert={convertFile} busy={!!readingFile} />,
          ]}
          minHeight={420}
          autoFocus={!note.title}
          style={{ border: 0 }}
        />
      </article>

      {suggesting && (
        <SuggestLinksModal
          noteId={noteId}
          text={suggesting.text}
          files={suggesting.files}
          unread={suggesting.unread}
          existingLinks={note.links}
          isPrivate={note.isPrivate}
          onClose={() => setSuggesting(null)}
          onLinked={(res) => { setNote(n => ({ ...n, links: res.links, isPrivate: res.isPrivate })); onChanged(); }}
        />
      )}

      {linking && (
        <LinkPicker
          existing={note.links}
          onClose={() => setLinking(false)}
          onPick={(ref) => { setLinking(false); updateLinks({ add: [ref] }); }}
        />
      )}
    </>
  );
}

function base64ToFile(base64, name, type) {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new File([bytes], name, { type });
}

function IconButton({ label, icon, onClick, active }) {
  return (
    <button type="button" aria-label={label} title={label} onClick={onClick}
      style={{ width: 34, height: 34, border: 0, borderRadius: 8, background: active ? '#fff3cc' : 'transparent', color: B.navy, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon name={icon} size={17} />
    </button>
  );
}

// The "Notes" nav entry: the Notes app, with the brain-dump capture funnel
// (write once, AI splits and routes to deals) one click away rather than gone.
//
// The capture opens blank (the list's "Brain-dump & route", the note header's
// route button) or SEEDED with a note ("Split & route" in the Harvi menu). A
// seeded split that files cleanly asks what to do with the original: it still
// holds the same text, so keeping both puts every section in search twice.
export function NotesHub({ onNoteOpen, CaptureComponent, initialNoteId, onInitialNoteHandled }) {
  const { toast } = useToast();
  const [capture, setCapture] = useState(null);   // null | { seed? }
  const [routed, setRouted] = useState(null);     // { notes, tasks } after a seeded split
  const [reopen, setReopen] = useState(null);     // the note to open on return
  const back = (noteId) => { setCapture(null); setRouted(null); setReopen(noteId || null); };

  if (capture && CaptureComponent) {
    const seed = capture.seed;
    const src = seed?.sourceNote;
    if (routed && src) {
      const filed = [routed.notes && `${routed.notes} note${routed.notes === 1 ? '' : 's'}`, routed.tasks && `${routed.tasks} task${routed.tasks === 1 ? '' : 's'}`].filter(Boolean).join(' and ');
      const retire = async () => {
        if (!src.isPrivate && !window.confirm('The original is linked to records, so deleting it removes it for everyone who can see them. You can restore it from Recently deleted for 30 days. Delete it?')) return;
        try { await api.del(`notebook/${src.id}`); toast('The original is in Recently deleted.'); back(null); }
        catch (e) { toast(e.message || 'Could not delete the original.', 'error'); }
      };
      return (
        <div style={{ maxWidth: 640, margin: '40px auto', padding: 24, background: B.surface, border: `1px solid ${B.divider}`, borderRadius: radius.xl }}>
          <h2 style={{ margin: '0 0 8px', fontSize: 18, color: B.text }}>Filed {filed} onto their deals.</h2>
          <p style={{ fontSize: 14, color: B.textMd, lineHeight: 1.5 }}>
            Your original note “{src.title}” still holds the same text. Keep both and every section shows up twice in search and in the deals' summaries.
            {routed.attached > 0 && ` Its files were copied onto ${routed.attached === 1 ? 'the routed note' : `${routed.attached} routed notes`}, so they are kept either way.`}
            {routed.attached === 0 && seed.files?.length > 0 && ' None of its files were attached to a routed note — keep the original if you need them.'}
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Btn variant="secondary" onClick={() => back(src.id)}>Keep the original</Btn>
            <Btn onClick={retire}>Move the original to Recently deleted</Btn>
          </div>
        </div>
      );
    }
    return (
      <div>
        <Btn variant="secondary" size="sm" onClick={() => back(src?.id)} style={{ marginBottom: 12 }}>{src ? '← Back to the note' : '← Back to notes'}</Btn>
        {src && seed.unread?.length > 0 && (
          <div style={{ maxWidth: 820, margin: '0 auto 8px', fontSize: 13, color: B.textMd }}>
            Not read: {seed.unread.map(u => `${u.name} (${u.reason})`).join('; ')}.
          </div>
        )}
        <CaptureComponent
          onNoteOpen={onNoteOpen}
          seedText={seed?.text || ''}
          seedFiles={seed?.files || []}
          embedded={!!src}
          onRouted={src ? setRouted : undefined}
        />
      </div>
    );
  }
  return (
    <NotesApp
      onOpenCapture={CaptureComponent ? (seed) => setCapture({ seed: seed || null }) : null}
      initialNoteId={reopen || initialNoteId}
      onInitialNoteHandled={() => { if (reopen) setReopen(null); else onInitialNoteHandled?.(); }}
    />
  );
}
